import { useEffect, useState } from 'react';
import { GOOGLE_PLACES_API_KEY } from 'constants/envConstants';

type ScriptStatus = 'loading' | 'ready' | 'error';

export const useGoogleMapsScript = (): ScriptStatus => {
  const [status, setStatus] = useState<ScriptStatus>('loading');

  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) {
      setStatus('error');
      return undefined;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );

    if (existingScript) {
      if (
        (window as { google?: { maps?: { places?: unknown } } }).google?.maps
          ?.places
      ) {
        setStatus('ready');
        return undefined;
      }
      const handleExistingLoad = () => {
        setStatus('ready');
      };
      const handleExistingError = () => {
        setStatus('error');
      };
      existingScript.addEventListener('load', handleExistingLoad);
      existingScript.addEventListener('error', handleExistingError);
      return () => {
        existingScript.removeEventListener('load', handleExistingLoad);
        existingScript.removeEventListener('error', handleExistingError);
      };
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      setStatus('ready');
    };

    const handleError = () => {
      setStatus('error');
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    return function cleanup() {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, []);

  return status;
};
